import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { Tooltip } from '@mui/material';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';

interface NedarimSyncRun {
  id: number;
  startedAt: string;
  completedAt: string | null;
  status: string;
  totalApiCalls: number;
  apiCallsWithResults: number;
  apiCallLimitTotal: number;
  blockedMosadotCount: number;
  totalTransactionsInserted: number;
  cancelledTransactionsCount: number;
  activeMosadotCount: number;
  syncedMosadotCount: number;
  skippedMosadotCount: number;
  inactiveDueCount: number;
  oldestDueAt: string | null;
  errorMessage: string | null;
  blockedMosadotIds: string | null;
}

const QUERY_URL = 'management/nedarim-sync-runs';
const DENSITY_STORAGE_KEY = 'table.density.nedarimSync';

const statusConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  Success: { label: 'הצלחה', color: '#15803d', bgColor: '#dcfce7' },
  StoppedByInternalLimit: { label: 'נעצר (limit פנימי)', color: '#a16207', bgColor: '#fef9c3' },
  BlockedByNedarim: { label: 'נחסם ע"י נדרים', color: '#dc2626', bgColor: '#fee2e2' },
  CredentialRejected: { label: 'מפתח API נדחה', color: '#7f1d1d', bgColor: '#fecaca' },
  Failed: { label: 'נכשל', color: '#dc2626', bgColor: '#fee2e2' },
  InProgress: { label: 'רץ כעת...', color: '#2563eb', bgColor: '#dbeafe' },
};

function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '-';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${remainingSeconds}ש'`;
  return `${minutes}ד' ${remainingSeconds}ש'`;
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
  return date.toLocaleString('he-IL', {
    timeZone: 'Asia/Jerusalem',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusChip({ status }: { status: string }) {
  const config = statusConfig[status] || statusConfig.Failed;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ color: config.color, backgroundColor: config.bgColor }}
    >
      {config.label}
    </span>
  );
}

function ApiUsageBar({ used, limit }: { used: number; limit: number }) {
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const barColor = percentage >= 90 ? '#dc2626' : percentage >= 70 ? '#f59e0b' : '#22c55e';

  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${percentage}%`, backgroundColor: barColor }}
        />
      </div>
      <span className="text-xs text-gray-600 whitespace-nowrap">
        {used} / {limit}
      </span>
    </div>
  );
}

const NedarimSyncDashboardPage = () => {
  const { t } = useTranslation();
  const { data: syncRuns, isLoading } = useGetAuthApi<NedarimSyncRun[]>(QUERY_URL);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const latestRun = syncRuns?.[0];

  const columns: ColumnDef<NedarimSyncRun>[] = [
    {
      id: 'startedAt',
      header: Col(t('nedarimSync.startedAt')),
      accessorKey: 'startedAt',
      cell: ({ row }) => formatDateTime(row.original.startedAt),
      meta: { className: 'min-w-[150px] px-2' },
    },
    {
      id: 'duration',
      header: Col(t('nedarimSync.duration')),
      accessorKey: 'completedAt',
      cell: ({ row }) => formatDuration(row.original.startedAt, row.original.completedAt),
      meta: { className: 'min-w-[90px] px-2' },
    },
    {
      id: 'status',
      header: Col(t('nedarimSync.status')),
      accessorKey: 'status',
      cell: ({ row }) => <StatusChip status={row.original.status} />,
      meta: { className: 'min-w-[150px] px-2' },
    },
    {
      id: 'totalApiCalls',
      header: Col(t('nedarimSync.totalApiCalls')),
      accessorKey: 'totalApiCalls',
      meta: { className: 'min-w-[140px] px-2' },
    },
    {
      id: 'apiUsage',
      header: Col(t('nedarimSync.apiUsage')),
      accessorKey: 'apiCallsWithResults',
      meta: { className: 'min-w-[140px] px-2' },
    },
    {
      id: 'transactionsInserted',
      header: Col(t('nedarimSync.transactionsInserted')),
      accessorKey: 'totalTransactionsInserted',
      meta: { className: 'min-w-[120px] px-2' },
    },
    {
      id: 'cancelled',
      header: Col(t('nedarimSync.cancelled')),
      accessorKey: 'cancelledTransactionsCount',
      meta: { className: 'min-w-[90px] px-2' },
    },
    {
      id: 'mosadot',
      header: Col(t('nedarimSync.mosadot')),
      accessorKey: 'syncedMosadotCount',
      cell: ({ row }) => {
        const { syncedMosadotCount, skippedMosadotCount, blockedMosadotCount, blockedMosadotIds } =
          row.original;
        const blockedIds = blockedMosadotIds ? JSON.parse(blockedMosadotIds) : [];
        const blockedTooltip = blockedIds.length > 0 ? `מוסדות חסומים: ${blockedIds.join(', ')}` : '';

        return (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-green-700">{syncedMosadotCount}</span>
            {skippedMosadotCount > 0 && (
              <span className="text-gray-400">/ {skippedMosadotCount} דולגו</span>
            )}
            {blockedMosadotCount > 0 && (
              <Tooltip title={blockedTooltip} arrow>
                <span className="text-red-600 cursor-help">/ {blockedMosadotCount} חסומים</span>
              </Tooltip>
            )}
          </div>
        );
      },
      meta: { className: 'min-w-[180px] px-2' },
    },
    {
      id: 'queue',
      header: Col(t('nedarimSync.queue')),
      accessorKey: 'inactiveDueCount',
      cell: ({ row }) => {
        const { inactiveDueCount, oldestDueAt } = row.original;
        if (inactiveDueCount === 0) return <span className="text-gray-400 text-xs">-</span>;

        const oldestTooltip = oldestDueAt
          ? `הוותיק בתור היה אמור להיסנכרן ב-${formatDateTime(oldestDueAt)}`
          : '';

        return (
          <Tooltip title={oldestTooltip} arrow>
            <span className={`text-xs ${oldestDueAt ? 'cursor-help' : ''}`}>
              <span className="text-green-700">{inactiveDueCount}</span>
              <span className="text-gray-400"> הגיע תורם</span>
            </span>
          </Tooltip>
        );
      },
      meta: { className: 'min-w-[130px] px-2' },
    },
    {
      id: 'error',
      header: Col(t('nedarimSync.error')),
      accessorKey: 'errorMessage',
      cell: ({ row }) =>
        row.original.errorMessage ? (
          <Tooltip title={row.original.errorMessage} arrow>
            <span className="text-red-600 text-xs truncate max-w-[200px] inline-block cursor-help">
              {row.original.errorMessage}
            </span>
          </Tooltip>
        ) : (
          '-'
        ),
      meta: { className: 'min-w-[120px] px-2' },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {latestRun && (
        <div className="bg-white rounded-lg border p-4 shadow-sm">
          <h3 className="text-sm font-medium text-gray-500 mb-3">{t('nedarimSync.lastRun')}</h3>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <StatusChip status={latestRun.status} />
              <span className="text-sm text-gray-600">{formatDateTime(latestRun.startedAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{t('nedarimSync.apiUsageLabel')}:</span>
              <ApiUsageBar used={latestRun.apiCallsWithResults} limit={latestRun.apiCallLimitTotal} />
            </div>
            <div className="text-sm text-gray-600">
              {t('nedarimSync.transactionsInserted')}: {latestRun.totalTransactionsInserted}
            </div>
            {latestRun.blockedMosadotCount > 0 && (
              <div className="text-sm text-red-600">
                {t('nedarimSync.blockedMosadot')}: {latestRun.blockedMosadotCount}
              </div>
            )}
          </div>
        </div>
      )}

      <DataTable
        title={t('nedarimSync.title')}
        data={syncRuns ?? []}
        isLoading={isLoading}
        columns={columns}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="nedarimSync"
        searchPlaceholder="nedarimSync.search"
        customGlobalSearch={() => true}
      />
    </div>
  );
};

export default NedarimSyncDashboardPage;
